import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Grid, Snackbar, Typography } from "@mui/material";
import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";
import HeaderCommerce from "./components/HeaderCommerce";
import TableCommerce from "./components/TableCommerce";
import CommerceDialog from "./components/CommerceDialog";
import { useComerciosJarvis } from "./hooks/useComerciosJarvis";
import {
  getComerciosAttributesService,
  getComerciosAddService,
  createComercioService,
  updateComercioService,
} from "modules/jarvis/services/commerceService";

export default function ComerciosJarvis() {
  const data = useComerciosJarvis();
  const [dialog, setDialog] = useState(null);
  const [attributes, setAttributes] = useState(null);
  const [available, setAvailable] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const optionsRevision = useRef(0),
    optionsController = useRef(null),
    mounted = useRef(false),
    writeLock = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      optionsRevision.current += 1;
      optionsController.current?.abort();
    };
  }, []);
  const loadOptions = async (mode) => {
    optionsController.current?.abort();
    const controller = new AbortController();
    optionsController.current = controller;
    const revision = ++optionsRevision.current;
    setLoadingOptions(true);
    setOptionsError(null);
    setAttributes(null);
    setAvailable([]);
    try {
      const [attrs, list] = await Promise.all([
        getComerciosAttributesService({ signal: controller.signal }),
        mode === "create"
          ? getComerciosAddService({ signal: controller.signal })
          : Promise.resolve({ success: true, data: [] }),
      ]);
      if (!mounted.current || revision !== optionsRevision.current) return;
      if (attrs.success) setAttributes(attrs.data);
      if (list.success) setAvailable(list.data);
      if (!attrs.success || !list.success)
        setOptionsError(
          attrs.error || list.error || "No se pudieron cargar las opciones del formulario."
        );
    } catch (err) {
      if (mounted.current && revision === optionsRevision.current) setOptionsError(err.message);
    } finally {
      if (mounted.current && revision === optionsRevision.current) {
        setLoadingOptions(false);
        optionsController.current = null;
      }
    }
  };
  const open = (commerce = null) => {
    if (writeLock.current) return;
    setDialog({ mode: commerce ? "edit" : "create", commerce });
    loadOptions(commerce ? "edit" : "create");
  };
  const close = () => {
    if (writeLock.current) return;
    optionsRevision.current += 1;
    optionsController.current?.abort();
    setDialog(null);
  };
  const refresh = async () => {
    const result = await data.refreshData();
    if (result && mounted.current)
      setNotice({
        severity: result.success ? "success" : "error",
        message: result.success ? "Listado actualizado correctamente." : result.error,
      });
  };
  const save = async (form) => {
    if (writeLock.current) return null;
    writeLock.current = true;
    setSaving(true);
    try {
      const result = await (dialog.mode === "edit"
        ? updateComercioService(form)
        : createComercioService(form));
      if (result.success && mounted.current) {
        const refreshResult = await data.refreshData();
        if (!refreshResult?.success)
          return {
            ...result,
            refreshWarning:
              "El comercio se guardó, pero no se pudo actualizar el listado. Cierra esta ventana y pulsa Actualizar.",
          };
      }
      return result;
    } finally {
      writeLock.current = false;
      if (mounted.current) setSaving(false);
    }
  };
  const updated = data.lastUpdated
    ? new Intl.DateTimeFormat("es-CO", {
        timeZone: "America/Bogota",
        dateStyle: "short",
        timeStyle: "medium",
      }).format(new Date(data.lastUpdated))
    : "Sin carga correcta";
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ py: 3 }}>
        <HeaderCommerce
          onRefresh={refresh}
          onCreateClick={() => open()}
          loading={data.loading || saving}
          filtros={data.filtros}
          onFiltroChange={data.setFiltro}
          onClearFilters={data.clearFilters}
          tiposComercio={data.tiposComercio}
        />
        <Grid container spacing={2} sx={{ my: 1 }}>
          {[
            ["Comercios en la vista", data.stats.totalComercios, "#e4f0fd", "#255b8f"],
            ["Activos", data.stats.activos, "#def3e8", "#206346"],
            ["Inactivos", data.stats.inactivos, "#fbe7ee", "#963b59"],
            ["Con apertura CB", data.stats.conAperturaCB, "#eae6fc", "#51479c"],
          ].map(([label, value, bg, color]) => (
            <Grid item xs={6} md={3} key={label}>
              <Box
                sx={{
                  bgcolor: bg,
                  border: `1px solid ${color}30`,
                  borderLeft: `4px solid ${color}`,
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography sx={{ fontSize: 26, fontWeight: 700, color }}>{value}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color }}>{label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Typography sx={{ my: 2, fontSize: 12, color: "#61798d" }}>
          Los contadores corresponden a los filtros · {data.stats.desconocidos} con estado sin
          información. Última carga correcta: {updated}
          {data.lastUpdated ? " · Colombia" : ""}
        </Typography>
        {data.error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" disabled={data.loading} onClick={refresh}>
                Reintentar
              </Button>
            }
          >
            {data.error}
            {data.lastUpdated ? " Se conserva el último listado cargado." : ""}
          </Alert>
        )}
        {data.loading && (
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
            <Typography sx={{ fontSize: 14 }}>Actualizando comercios…</Typography>
          </Box>
        )}
        {(!data.loading || data.todosComercios.length > 0) && (
          <TableCommerce
            comercios={data.comercios}
            totalComercios={data.todosComercios.length}
            onEditClick={open}
            disabled={saving}
          />
        )}
        <CommerceDialog
          open={!!dialog}
          commerce={dialog?.commerce}
          attributes={attributes}
          availableComercios={available}
          loading={loadingOptions}
          loadError={optionsError}
          onRetry={() => loadOptions(dialog.mode)}
          onClose={close}
          onSave={save}
        />
        <Snackbar open={!!notice} autoHideDuration={5000} onClose={() => setNotice(null)}>
          <Alert severity={notice?.severity || "info"} onClose={() => setNotice(null)}>
            {notice?.message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
}
